<template>
  <div class="patient-card">
    <div class="descriptor-container">
      <span class="descriptor-label">{{ tr('Name') }}</span> <span class="datum">{{ patientData.firstName }}</span>
      <span class="datum">{{ patientData.lastName }}</span>
    </div>
    <div class="descriptor-container">
      <span class="descriptor-label">{{ tr('MRN') }}</span> <span class="datum">{{ patientData.mrn }}</span>
    </div>
    <div class="age-sex">
      <div class="descriptor-container">
        <span class="descriptor-label age">{{ tr(ageLabel) }}</span> <span class="datum">{{ patientData.age }}</span>
      </div>
      <div class="descriptor-container">
        <span class="descriptor-label">{{ tr('Sex') }}</span> <span class="datum">{{ patientData.sex }}</span>
      </div>
    </div>
    <div v-if="patientData.mortalityStatus" class="descriptor-container">
      <span class="descriptor-label">{{ tr('Mortality Status') }}</span> <span class="datum">{{
        patientData.mortalityStatus }}</span>
    </div>
    <div v-if="patientData.diagnosis" class="descriptor-container">
      <span class="descriptor-label">{{ tr('Primary Diagnosis') }}</span> <span class="datum">{{ patientData.diagnosis
        }}</span>
    </div>
    <div v-if="!featureToggles.providenceBuild && patientData.staging" class="descriptor-container">
      <span class="descriptor-label">{{ tr('Clinical Stage') }}</span> <span class="datum">{{ patientData.staging
        }}</span>
    </div>
  </div>
</template>

<script setup>
// Copyright (c) 2020-2023 dātma, inc™
// Copyright (c) 2023 datma, inc.
//
// Unauthorized copying of this file, via any medium is strictly prohibited
// Proprietary and confidential

import { computed, inject, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { diagnose, getId } from './patientEhr.js'
import { getRecord } from '@/ux3/cards/PatientCard/patientEhr.js'
import { calculateAge } from '@/utils/utils'

const tr = inject('tr')
const store = useStore()
const featureToggles = computed(() => store.getters.featureToggles)
const ageLabel = ref('Age')

const patientData = reactive({
  firstName: '',
  lastName: '',
  sex: '',
  mrn: '',
  age: 0,
  diagnosis: '',
  staging: '',
  mortalityStatus: '',
})

const patient = computed(() => store.getters.session.patient)

const loadPatientData = async () => {
  patientData.firstName = patient.value.given || '—'
  patientData.lastName = patient.value.family || '—'
  patientData.sex = patient.value.gender || '—'
  patientData.mrn = (patient.value.id?.length > 0 ? patient.value.id.split(',') : ['—'])[0]
  const birthdate = patient.value.birthdate || '—'
  patientData.age = calculateAge(new Date(birthdate)) || '—'
  patientData.mortalityStatus = patient.value.mortalityStatus || '—'
  ageLabel.value = patientData.mortalityStatus === 'Deceased' ? 'Age at Time of Death' : 'Age'

  if (featureToggles.value.providenceBuild) {
    // EHR backend
    const patientId = await getId(patientData.mrn)
    const diagnosis = await diagnose(patientId)
    patientData.diagnosis = (diagnosis.sample_description || { 0: '—' })[0]
    return
  }

  // Tumorboard backend
  const record = await getRecord(patientData.mrn)
  if (!record?.length) { return }
  record.sort(compareDates)
  const currentRecord = record[0]
  patientData.diagnosis = currentRecord.diagnosis || '—'
  patientData.staging = Object.keys(currentRecord.recording_info.clinical_staging)[0].split('Stage ')[1] || '—'
}

// sort by date descending (newest to oldest)
const compareDates = (a, b) => {
  if (a.date < b.date) {
    return 1
  }
  if (a.date > b.date) {
    return -1
  }
  return 0
}

watch(patient, () => {
  loadPatientData()
})

await loadPatientData()
</script>

<style lang="scss" scoped>
.patient-card {
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  flex-direction: column;
  text-align: left;
  font-size: calc(8px + 0.3vw);
  overflow: auto;
}

.descriptor-container {
  margin: 15px;
  display: inline-block;
  margin-right: 0.9vmax;
  align-content: left;
}

.datum {
  font-weight: 500;
}

.age-sex {
  display: flex;
  text-transform: capitalize;
}

.age {
  min-width: 100px;
  width: 100%;
}

.descriptor-label {
  color: var(--generalDescriptorTextColor);
  display: block;
  align-content: left;
  text-transform: capitalize;
}
</style>
